/**
 * UpSurv 2.0 — core contract types.
 *
 * This file is the interface every other module builds against: the survey JSON
 * document, the question catalog, the calculation engine, the validation engine,
 * the offline store, and the future estimate engine.
 *
 * Two rules govern the shape and both come from the brief (§3):
 *
 *  1. Question IDs from specification §3 are STABLE KEYS. The estimate engine
 *     keys catalog line items off them. Never renumber, never reuse.
 *  2. Structure and answers are separated. The entity tree (rooms, cars, groups,
 *     hoistways) is canonical structure; everything else is a keyed answer.
 *     M1/M2/M3/M6/M7 are *structural* questions — they are captured in the tree,
 *     not in `answers`, because the rest of the form is generated from them.
 */

/**
 * **2.9.0** since ICRA barricade moves (2026-10-30, D-058): three question ids
 * join the catalog — `L9d` (will the ICRA barricades move to another
 * elevator?, on L9a = ICRA), and on its Yes `L9e` (how many more elevators)
 * and `L9f` (how many cars are worked on at the same time).
 * Additive, so the MINOR moves by docs/ESTIMATE-CONTRACT.md §9's rule. A 2.8.0
 * document parses unchanged; one that lacks the new answers is asked L9d only
 * where its barricades are ICRA, and a missing L9d/L9e reads as "not asked",
 * never as No or 0 (L9f likewise).
 *
 * **2.8.0** since Chip's follow-ups (2026-10-27, D-054): six question ids join
 * the catalog — `G8a`, `G9a`, `G9b`, `G14a`, `O6a`, `A3a`, each a child shown
 * on its parent's Yes — and `QuestionDef` gains `min`/`max` (catalog-only, not
 * on the document). Additive, so the MINOR moves by docs/ESTIMATE-CONTRACT.md
 * §9's rule — the rule D-053 applied when it stayed at 2.7.0 because it added
 * no id. A 2.7.0 document parses unchanged; one that lacks the new answers is
 * only asked them where a parent is Yes.
 *
 * **2.7.0** since Chip's wording round (2026-09-11, D-045 and D-046): `L3a`
 * joins the catalog; `F19`, `E9b`, `E9c` and `L3` leave it — ids retired,
 * never reused, and an answer already stored under any of them stays on the
 * record, inert (nothing asks it, nothing prices it; F19's still prints on the
 * cut sheet). Minor, not major, because no stored document parses into the
 * wrong thing: `AnswerValueSchema` is the union it was and an unknown answer
 * key has always been legal. D-053 (Chip, 2026-10-27) leaves it at 2.7.0
 * although 2.7.0 has shipped: `P12` and `P14` are retired the same way (ids
 * never reused, stored answers inert), and the rest is wording and A2's
 * `required` — no id added, no shape changed, so nothing parses differently.
 *
 * **2.6.0** since Amendment 8 (2026-09-06, D-041): a shot marked `required` on
 * the catalog now blocks like an unanswered question — the reason
 * `'missing_required_photo'`, joining `BLOCKING_GAP_REASONS` — and `Note`
 * gains `shotListId`, the written reason a surveyor attaches to a required
 * shot they could not take. Additive: a `2.5.x` document parses unchanged,
 * and nothing already on the record used the new field.
 *
 * **2.2.0** since Amendment 6 (2026-08-18): Fire Life Safety is a section of its
 * own — a new `SectionKind` (`fls`), so a new value can appear in every
 * `sectionInstanceId` field (photos, notes, gaps, missing photos). Additive by
 * docs/ESTIMATE-CONTRACT.md §9's rule, so minor; every 2.1.x document parses
 * here unchanged (the two FACP shots' old address is still honoured — see
 * `RecommendedShot.formerly`).
 *
 * **2.1.0** since spec Amendment 5 (2026-08-07). Minor, not major, and the rule
 * for that is stated in docs/ESTIMATE-CONTRACT.md §9: additive changes — new
 * question ids (§3.11's F block), new derived keys (F1), a field that became
 * optional (`WallRange.wallType`) — bump the minor; only a change that would
 * make an existing stored document parse into the WRONG THING bumps the major.
 * Every 2.0.0 document still parses here unchanged.
 */
export const SCHEMA_VERSION = '2.10.0';

import type { FlsCutSheet } from './flsCutSheet';

/* ─────────────────────────── enumerations ─────────────────────────── */

/** Room type is the first question of every machine room survey (spec Q3/M1). */
export type RoomType = 'traction' | 'hydro' | 'mrl';

/** MRL runs the Hydro question set, tagged. This is the resolved question set. */
export type FormSet = 'traction' | 'hydro';

export type SurveyOrigin = 'assigned' | 'field_initiated';

export type SurveyStatus =
  | 'assigned'
  | 'in_progress'
  | 'submitted_clean'
  | 'submitted_with_gaps';

/**
 * Whether a status means "this survey has been submitted".
 *
 * Lives here, in the domain types, because three layers have to agree on it and
 * a second copy would be free to drift: the D1 schema's `is_submitted` CHECK
 * (src/db/queries.ts), the API's refusal to take a submitted document through
 * `PUT /api/surveys/:id` (functions/api), and the device's rule that a submitted
 * document has no valid document push (src/db/store.ts, src/db/sync.ts).
 */
export function isSubmittedStatus(status: SurveyStatus): boolean {
  return status === 'submitted_clean' || status === 'submitted_with_gaps';
}

/** Pricing mode legend, spec §3. */
export type PricingMode = 'AUTO' | 'SURV-QTY' | 'FLAT' | 'INFO' | 'OFFICE';

/** Which entity level owns a question — determines repetition. */
export type EntityLevel =
  | 'building'
  | 'room'
  | 'group'
  | 'hoistway'
  | 'survey';

/**
 * Section kinds. `overhead` and `pit` repeat per hoistway; `lobby` repeats per
 * group; `fls` (Amendment 6) and `adhoc` are survey-level and appear once, at
 * the end of the walk — the fire alarm panel and the extras checklist are the
 * last things a surveyor stands in front of, not the first.
 */
export type SectionKind =
  | 'building'
  | 'room_setup'
  | 'electrical'
  | 'hvac'
  | 'general'
  | 'overhead'
  | 'pit'
  | 'lobby'
  | 'cab'
  | 'access'
  | 'fls'
  | 'adhoc';

export type SectionStatus = 'todo' | 'in_progress' | 'done' | 'flagged';

/* ─────────────────────────── answer values ─────────────────────────── */

export interface Contact {
  name?: string;
  phone?: string;
  email?: string;
}

/**
 * A span of landings entered as ONE row — "2–20 sheetrock" — so a 20-landing
 * job never costs the surveyor nineteen taps (spec Q11).
 *
 * `wallType` is OPTIONAL, and the reason is Amendment 5. Two questions now use
 * range entry and only one of them is about a material:
 *
 *  - **L4a** — wall type per landing. The material IS the answer: it selects the
 *    per-landing cut-and-patch price, so every L4a row carries one.
 *  - **F11a / F12a** — the landings that are missing a smoke detector, and the
 *    landings that are sprinklered (spec §3.11). These are spans of landings and
 *    nothing else; there is no material to state.
 *
 * The alternative was a second answer kind, which would have meant a second
 * range control on the screens and a second thing to keep in step with this one.
 * The alternative *inside* this shape — keeping `wallType` required and making
 * the FLS rows carry a material nobody looked at — would have put invented
 * survey data on the record, which is the one thing the form must never do.
 * Absent means "this row is not about a material", never "sheetrock".
 */
export interface WallRange {
  fromLanding: number;
  toLanding: number;
  /** Present on L4a rows; absent on the FLS landing spans (F11a, F12a). */
  wallType?: WallType;
}

export type WallType =
  | 'sheetrock'
  | 'block'
  | 'brick'
  | 'cement'
  | 'tile'
  | 'marble'
  // Amendment 5, 2026-08-07: common on the Gulf Coast exterior lobbies OSB
  // works in, and a materially different cut-and-patch price from block.
  | 'stucco';

/**
 * The "Y/N + count" shape used throughout §3 (E1a, E1b, E2, E3, E5a, E5b, E13,
 * E14, P9a, P9b, G11, L7, O5, O9, C1). `flag:false` always implies count 0.
 * L3 was one until D-045/D-046 (2026-09-11) retired it; a document written
 * before that build still carries this shape under its key, unread.
 */
export interface FlagCount {
  flag: boolean;
  count: number;
}

export interface Measure {
  /** Single-dimension runs: condensate (H3), horizontal conduit (E7c). */
  feet?: number;
  /** Two-dimension rooms: mini-split sizing (H2). Office picks tonnage. */
  lengthFt?: number;
  widthFt?: number;
}

export type AnswerValue =
  | { kind: 'text'; value: string }
  | { kind: 'number'; value: number }
  | { kind: 'bool'; value: boolean }
  | { kind: 'tristate'; value: 'yes' | 'no' | 'unknown' }
  | { kind: 'select'; value: string }
  | { kind: 'multiselect'; value: string[] }
  | { kind: 'contact'; value: Contact }
  | { kind: 'date'; value: string }
  | { kind: 'measure'; value: Measure }
  | { kind: 'ranges'; value: WallRange[] }
  | { kind: 'flagCount'; value: FlagCount };

export type AnswerKind = AnswerValue['kind'];

/* ────────────────────────── entity structure ────────────────────────── */

export interface CarRef {
  id: string;
  /** Surveyor-facing label, e.g. "Car 1". */
  label: string;
  /**
   * Whether this car runs on the automatic transfer switch. Required to derive
   * B17 (ATS qty = machine rooms containing ATS cars). See docs/ADR-002.
   */
  onAts: boolean;
}

/** A bank of cars sharing a riser, hall stations, and a Main Egress (spec Q6). */
export interface Group {
  id: string;
  name: string;
  carIds: string[];
  /** Landings served by this group. Landings ≈ floors; 1 landing ≈ 9 ft (Q5). */
  landings: number;
}

/** A shaft. Owns the Overhead and Pit question sets (spec Q7–8). */
export interface Hoistway {
  id: string;
  name: string;
  carIds: string[];
}

export interface MachineRoom {
  id: string;
  /** M2 — surveyor-assigned, e.g. "Penthouse Machine Room". */
  name: string;
  /** M1 — first question asked; drives the question set. */
  type: RoomType;
  /** M3 — the car list. Room unit count is derived from this, never typed (Q4). */
  cars: CarRef[];
  /** M6 */
  groups: Group[];
  /** M7 */
  hoistways: Hoistway[];
}

export interface BuildingInfo {
  /** Structural only; all of B1–B16 live in `answers` keyed by question ID. */
  id: string;
}

/* ───────────────────────── ad hoc items (§2.5) ───────────────────────── */

export interface AdHocOther {
  id: string;
  description: string;
}

export interface AdHocItems {
  storageRental: boolean;
  dumpsterRental: boolean;
  /** Quantity = number of dirty pits; catalog prices per pit. */
  cleanPitOfOilAndDebris: FlagCount;
  parking: boolean;
  other: AdHocOther[];
}

/* ───────────────────────── notes / photos / gaps ───────────────────────── */

/**
 * A note names a question, OR — Amendment 8 — a shot it is the written reason
 * for (`shotListId`); never both. A shot note is how a required photo not
 * taken is spoken for (D-041), the role D-017's acknowledgement had.
 */
export interface Note {
  id: string;
  sectionInstanceId: string;
  /** Absent = section-level note. Present = attached to a specific question. */
  questionId?: string;
  /** Amendment 8, D-041 — the shot this note is the written reason for. */
  shotListId?: string;
  text: string;
  createdAt: string;
}

export type PhotoUploadState = 'pending' | 'uploading' | 'uploaded' | 'failed';

export interface PhotoRef {
  id: string;
  surveyId: string;
  sectionInstanceId: string;
  /** Absent = section-level photo. */
  questionId?: string;
  /** Set when this photo satisfies a recommended shot from the section shot list. */
  shotListId?: string;
  /**
   * A caption the surveyor typed for this one photo, shown under it on the
   * phone — **2.4.0**, D-038. Absent when the surveyor never labelled it.
   * Written by `store.setPhotoLabel()`, which deletes the field on an empty or
   * whitespace-only value rather than storing blank text.
   */
  label?: string;
  /** R2 object key — the photo system of record (brief §3). */
  r2Key: string;
  contentType: string;
  bytes: number;
  capturedAt: string;
  uploadState: PhotoUploadState;
  /** Attempts made by the sync engine; used for backoff, not for dropping. */
  attempts: number;
}

export type GapReason =
  | 'unanswered'
  | 'validation_failed'
  | 'missing_photo'
  | 'missing_required_photo'
  | 'surveyor_flagged';

/**
 * The surveyor saying, in their own words, that an outstanding answer could not
 * be captured on this job — **D-017**.
 *
 * ── What it is, and the one thing it is not ──────────────────────────────────
 * Specification §2.3 requires BOTH of these to stay true at once: a survey with
 * a genuine gap must still submit ("the pit was locked"), and the surveyor must
 * never be able to clear a gap. An acknowledgement is how those meet. It does
 * not remove a gap, downgrade it, or change its reason: the gap is still
 * detected, still blocking, still travels with the record, and still lands on
 * Chip's and Tim's desk. All it does is attach the REASON — which arrives as the
 * gap's `surveyorNote` — and record that a person deliberately left it that way.
 *
 * That is the whole distinction the submit gate is built on: an answer nobody
 * ever looked at is ACCIDENTAL and must not go silently; an answer somebody
 * stood in front of and could not get is DELIBERATE and must still submit.
 *
 * ── Keyed to the gap, not to the question ───────────────────────────────────
 * `gapId` is the id `detectGaps()` mints, which is derived from the section and
 * the question and is therefore stable across runs and devices. An
 * acknowledgement whose gap is no longer detected — because the surveyor went
 * back and answered it — simply matches nothing. It is inert, not stale state to
 * be reconciled: answering a question is how a gap is genuinely resolved, and
 * the record of having once marked it unavailable does no harm.
 */
export interface GapAcknowledgement {
  /** The `Gap.id` this acknowledges. Minted by the engine, never by a screen. */
  gapId: string;
  sectionInstanceId: string;
  /** The question the gap is about. Absent only for a gap that names none. */
  questionId?: string;
  /** The surveyor's own words. Becomes the gap's `surveyorNote`. May be empty. */
  note: string;
  acknowledgedAt: string;
}

/**
 * A gap travels with the record and appears in the submission notification.
 * Only Chip/Tim resolve gaps — the surveyor cannot self-clear one (spec §2.3).
 */
export interface Gap {
  id: string;
  sectionInstanceId: string;
  sectionTitle: string;
  questionId?: string;
  reason: GapReason;
  message: string;
  /** Surveyor's explanation, e.g. "pit was locked out". */
  surveyorNote?: string;
}

/* ──────────────────────── sections & shot lists ──────────────────────── */

/** Scope identifies which repetition of a section or answer we mean. */
export interface Scope {
  roomId?: string;
  groupId?: string;
  hoistwayId?: string;
}

export interface RecommendedShot {
  id: string;
  label: string;
  /** Only prompted when this predicate holds (e.g. condensate route if Mini-Split). */
  conditional?: boolean;
  /**
   * WHERE this shot used to live, when it has moved (Amendment 6, 2026-08-18).
   *
   * The FACP and its sticker were prompted on the building section as
   * `building.facp` / `building.facp_sticker` before Fire Life Safety became a
   * section of its own. A photo already on a record under that address is the
   * same picture, taken by a surveyor who was standing in front of the panel;
   * telling the office it was never taken because the prompt moved would be the
   * form lying about its own history. So a shot may name its former address and
   * a photo filed there covers it — read in exactly two places, `missingShots`
   * and `sectionCloseView`, so the record and the screen cannot disagree.
   */
  formerly?: { sectionInstanceId: string; id: string };
  /** Spec §4 rule 8's exception (Amendment 8, D-041): a shot the survey needs. */
  required?: true;
  /** One row per car in the room; the row's id is `${id}:${carId}` (Amendment 8). */
  perCar?: true;
}

export interface SectionInstance {
  /** Canonical, stable, e.g. "room:r1|pit:h2". Built by sectionInstanceId(). */
  id: string;
  kind: SectionKind;
  scope: Scope;
  /** Surveyor-facing, e.g. "Pit · HW 2". */
  title: string;
  /** Position in the default fixed walk order (spec §2.2). */
  order: number;
  shotList: RecommendedShot[];
}

/* ─────────────────────── question catalog (spec §3) ─────────────────── */

/** A condition on another answer that gates or branches this question. */
export interface QuestionCondition {
  /** Question ID whose answer is tested, resolved in the same scope. */
  questionId: string;
  /** Question is shown when the referenced answer matches. */
  equals?: boolean | string | number;
  /** For flagCount answers: test the flag. */
  flagIs?: boolean;
  /** Structural predicate instead of an answer, e.g. room type. */
  structural?: 'roomTypeIsTraction' | 'roomTypeIsHydro';
}

/**
 * One choice on a select/multiselect question.
 *
 * `formSets` exists because some questions keep a stable ID while their valid
 * choices differ by room type — M5 (room location) is Overhead/Basement for a
 * Traction room and Adjacent-lowest/Adjacent-top/Remote for a Hydro or MRL one.
 * The tag lives on the option itself rather than in a lookup table beside the
 * catalog so that reading `question.options` — the obvious thing to do — cannot
 * silently hand a caller the wrong room type's choices. Filter at the call site:
 *
 *   options.filter(o => !o.formSets || o.formSets.includes(formSet))
 */
export interface QuestionOption {
  value: string;
  label: string;
  hint?: string;
  /** Absent = valid in every form set. */
  formSets?: FormSet[];
  /**
   * A gate on the OPTION rather than on the question — **D-024**.
   *
   * F8 (the machine-room relay inventory) is asked in every machine room, but
   * one of its four boxes — **Shunt** — exists only where there is a sprinkler
   * to shunt for (M4 = Yes). Splitting F8 into two questions would put the same
   * checklist under two ids and make the office reconcile them; hiding the whole
   * question would lose the Primary / Secondary / Flashing Hat inventory in
   * every dry machine room.
   *
   * Same shape and same evaluator as `QuestionDef.showWhen`, so nothing new had
   * to learn how to read a gate. It is `formSets` (D-007) taken one step
   * further: the narrowing lives on the option, where reading `question.options`
   * cannot silently hand a caller a choice that does not apply.
   *
   * **Evaluating it needs the document.** `optionsFor(question, doc, scope)` in
   * validation.ts is the full narrowing and is what a screen must call.
   * `visibleOptions(question, formSet)` in catalog.ts has no document, so it
   * drops a conditional option outright — an unanswered gate is a closed gate,
   * the same rule both engines already apply.
   */
  showWhen?: QuestionCondition[];
}

export interface QuestionDef {
  /** Spec §3 ID — the stable key. */
  id: string;
  level: EntityLevel;
  section: SectionKind;
  /** Surveyor-facing prompt. */
  label: string;
  /** Sub-label / clarifier shown under the prompt. */
  hint?: string;
  kind: AnswerKind;
  pricing: PricingMode;
  /** Unit of measure for quantified lines. */
  uom?: string;
  /** Options for select/multiselect. */
  options?: QuestionOption[];
  /** Which form sets this question appears in. Absent = both. */
  formSets?: FormSet[];
  /** All conditions must hold for the question to be shown. */
  showWhen?: QuestionCondition[];
  /**
   * AUTO questions are computed by the calc engine; the surveyor never enters a
   * quantity. The Y/N gate may still be surveyor-answered (e.g. E6, E8).
   */
  auto?: boolean;
  /** Counts toward gap detection at submit when unanswered. */
  required?: boolean;
  /**
   * `number` only: the smallest and largest count the box takes — **D-054**.
   * Handed to the Stepper, which clamps a typed number into them
   * (`parseTypedCount`, D-053) and disables −/+ at the ends. Absent = 0 and no
   * ceiling, as before. Not a validation rule: a stored answer outside them
   * (an older record) still loads, validates and submits as it is.
   */
  min?: number;
  max?: number;
  /** Ordering within the section. */
  order: number;
}

/* ────────────────────── calculation & validation ────────────────────── */

/**
 * One computed quantity. This is simultaneously the "⚙ calculated for you" chip
 * the surveyor sees and the line the estimate engine consumes.
 */
export interface DerivedLine {
  questionId: string;
  scope: Scope;
  quantity: number;
  uom: string;
  /** Plain-language math shown to the surveyor, e.g. "6 cars ÷ 3, rounded up". */
  basis: string;
  pricing: PricingMode;
}

export type ValidationSeverity = 'error' | 'warning' | 'info';

/**
 * The shunt refurb→replace flip (spec §4 rule 5) is the only validation that
 * mutates answers. It carries the payload the form applies plus the explanation
 * the surveyor is shown.
 */
export interface AutoFix {
  kind: 'shunt_refurb_to_replace';
  scope: Scope;
  set: { questionId: string; value: AnswerValue }[];
  explanation: string;
}

/**
 * The arithmetic behind a counting rule, in numbers rather than in a sentence.
 *
 * §4 rules 1, 2, 3, 4 and 6 all say the same shape of thing — *"what you have
 * answered so far adds up to N, and the structure says it must reach M"* — and
 * every one of them already puts both numbers into its `message`. This carries
 * them structurally as well, so a screen can render "0 of 2 cars accounted for"
 * beside the two steppers, or a headline can name the rule that is blocking a
 * section, WITHOUT parsing English out of the engine's sentence.
 *
 * It changes nothing the engine computes. The numbers here are the same numbers
 * the rule compared to decide it had a finding at all.
 */
export interface IssueTally {
  /**
   * The short name of the thing being counted — "Cab light disconnects".
   * The rule's own noun, so a headline can name the rule without slicing it
   * back out of `message`.
   */
  subject: string;
  /** What the surveyor's answers add up to right now. */
  running: number;
  /** What the structure says it must reach — usually the car count. */
  target: number;
  /** What is being counted, singular: 'car'. */
  unit: string;
  /**
   * `exact` — running must EQUAL target (disconnects: leave-as-is is not a
   * disposition). `ceiling` — running must not EXCEED it (pit ladders: a
   * code-compliant remainder stays as it is). Never unify the two (§4 note).
   */
  shape: 'exact' | 'ceiling';
}

export interface ValidationIssue {
  /** Spec §4 rule number, e.g. "V2". */
  ruleId: string;
  severity: ValidationSeverity;
  scope: Scope;
  /** Questions the surveyor should look at. */
  questionIds: string[];
  /** Plain-language, shown in the live validation chip. */
  message: string;
  explanation?: string;
  autoFix?: AutoFix;
  /** The counting behind an arithmetic rule — see `IssueTally`. */
  tally?: IssueTally;
}

/* ─────────────────────────── survey document ─────────────────────────── */

/** Canonical key for an answer: question ID scoped to its entity repetition. */
export type AnswerKey = string;

export interface SurveyDocument {
  schemaVersion: string;
  surveyId: string;
  origin: SurveyOrigin;
  status: SurveyStatus;
  /** Lightweight auth: the surveyor's access token subject (brief §3). */
  assignedTo?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  /**
   * The name of the surveyor who submitted, stamped from the phone's identity
   * at submission — **2.4.0**, D-038. Absent on a document never submitted,
   * and on every document written before this field existed.
   */
  submittedByName?: string;
  building: BuildingInfo;
  rooms: MachineRoom[];
  adHoc: AdHocItems;
  /** Every non-structural answer, keyed by answerKey(). */
  answers: Record<AnswerKey, AnswerValue>;
  /**
   * Answer keys whose current value was written by the form's own defaulting
   * pass rather than by the surveyor — **D-016**.
   *
   * Every answerable Yes/No question opens already reading **No**, and that No
   * is a REAL answer in `answers`: the design authority's instruction is that a
   * surveyor should not have to touch a question that does not apply, and that a
   * survey should not be submittable full of questions nobody ever saw. A
   * display-only default would have left the document unanswered and the
   * submission full of gaps, which is the defect being fixed.
   *
   * But "No because the surveyor said No" and "No because the form filled it in"
   * are different claims, and exactly one thing in the app must be able to tell
   * them apart: the D-014/D-015 timing gate, which may not call a disconnect
   * split WRONG until the surveyor has actually engaged with both halves of it.
   * Without this list, a six-car room's Electrical section would default to
   * 0 + 0 ≠ 6 and turn red the instant it opened — the precise defect D-015 was
   * raised about. See `src/ui/state/answerDefaults.ts`.
   *
   * A key leaves this list the moment the surveyor writes that answer
   * themselves, including when they write the same value the default held.
   * Absent (or empty) means every answer present is the surveyor's own.
   */
  defaultedAnswers?: AnswerKey[];
  /**
   * Outstanding answers the surveyor has explicitly marked unavailable — D-017.
   *
   * Written at the last look before submit, one per gap, carrying the reason.
   * It never removes or downgrades a gap; see `GapAcknowledgement`. Optional
   * because it is the exception rather than the rule, and because every document
   * written before D-017 has none.
   */
  acknowledgedGaps?: GapAcknowledgement[];
  notes: Note[];
  photos: PhotoRef[];
  /** Populated at submit; empty on a clean survey. */
  gaps: Gap[];
}

/** A submitted survey plus everything the estimate engine needs, denormalized. */
export interface SubmittedSurvey extends SurveyDocument {
  derived: DerivedLine[];
  validation: ValidationIssue[];
  missingPhotos: {
    sectionInstanceId: string;
    sectionTitle: string;
    shotLabel: string;
    /** A FLAG only (Amendment 8, D-041) — copied from `MissingShot.required`. */
    required?: true;
  }[];
  /**
   * Ready-made sheets for the subcontractor reports the office prints from a
   * submission — D-027. Today one: Fire Life Safety, the sheet the fire alarm
   * company quotes from. Like `derived` and `validation` it is a pure function
   * of the document (`buildFlsCutSheet`), composed rather than typed, so it
   * cannot say anything the answers do not.
   */
  cutSheets: { fls: FlsCutSheet };
}

/* ───────────────────────────── notifications ───────────────────────────── */

/**
 * `survey_recalled` is the office sending a submitted survey back to the
 * surveyor (D-018). It is the one event that can fire more than once for a
 * survey — a recall is a thing the office DOES, not a milestone a survey passes
 * through once — so it is deduped by the reopen revision rather than by
 * (survey, event). See `ux_notifications_survey_event` in src/db/d1-schema.sql.
 */
export type NotificationEvent =
  | 'survey_created_field'
  | 'survey_submitted'
  | 'survey_recalled';

export interface NotificationPayload {
  event: NotificationEvent;
  surveyId: string;
  buildingName: string;
  /** The one printed line — `formatAddress` over B2 + B2a–c (D-039). */
  address: string;
  /** The parts beside it, present only when the record holds them. */
  city?: string;
  state?: string;
  zip?: string;
  surveyor: string;
  createdAt: string;
  /** Submission only. */
  status?: SurveyStatus;
  roomCount?: number;
  unitCount?: number;
  gaps?: Gap[];
  /** Recommended shots not taken (§4 rule 8) — excludes required shots. */
  missingPhotoCount?: number;
  /**
   * REQUIRED shots not taken (Amendment 8, D-041) — additive, alongside
   * `missingPhotoCount` rather than folded into it, because the two block
   * differently and the office reads them as two separate lines.
   */
  requiredPhotoCount?: number;
  /**
   * The surveyor's section-level notes, in walk order — D-033 R15 made the
   * section note the one place a blocked section is written down ("Pit was
   * locked out — the super has the key"), so the office's message carries
   * them beside the gaps. Question-level notes stay on the record.
   */
  notes?: NotificationNote[];
}

export interface NotificationNote {
  /** Room-qualified section title, the way a gap names its section. */
  where: string;
  text: string;
}

/* ──────────────────────────── key helpers ──────────────────────────── */

/**
 * Canonical answer key. Scope segments are ordered room → group → hoistway so
 * keys sort predictably and are greppable in a submitted document.
 *
 *   answerKey('B11')                                  -> "B11"
 *   answerKey('E1a', { roomId: 'r1' })                -> "room:r1|E1a"
 *   answerKey('P1',  { roomId: 'r1', hoistwayId:'h2'})-> "room:r1|hw:h2|P1"
 *   answerKey('L2',  { roomId: 'r1', groupId: 'gA' }) -> "room:r1|grp:gA|L2"
 */
export function answerKey(questionId: string, scope: Scope = {}): AnswerKey {
  const parts: string[] = [];
  if (scope.roomId) parts.push(`room:${scope.roomId}`);
  if (scope.groupId) parts.push(`grp:${scope.groupId}`);
  if (scope.hoistwayId) parts.push(`hw:${scope.hoistwayId}`);
  parts.push(questionId);
  return parts.join('|');
}

/** Canonical section instance ID, same scoping convention as answerKey. */
export function sectionInstanceId(kind: SectionKind, scope: Scope = {}): string {
  const parts: string[] = [];
  if (scope.roomId) parts.push(`room:${scope.roomId}`);
  if (scope.groupId) parts.push(`grp:${scope.groupId}`);
  if (scope.hoistwayId) parts.push(`hw:${scope.hoistwayId}`);
  parts.push(kind);
  return parts.join('|');
}

/** MRL runs the Hydro question set (spec Q3). */
export function formSetFor(type: RoomType): FormSet {
  return type === 'traction' ? 'traction' : 'hydro';
}

/** R2 object key for a photo — the tagging scheme from brief §3. */
export function photoR2Key(
  surveyId: string,
  sectionInstanceId: string,
  photoId: string,
  ext: string,
  questionId?: string,
): string {
  const q = questionId ? `question/${questionId}/` : '';
  const safeSection = sectionInstanceId.replace(/[|:]/g, '_');
  return `surveys/${surveyId}/section/${safeSection}/${q}${photoId}.${ext}`;
}
